import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  Button,
  Label,
  Textarea,
  Badge,
  Card,
  CardContent,
} from '../../../components/ui';
import {
  fetchTicketingRequestMessages,
  postTicketingRequestMessage,
  downloadTicketingAttachmentObjectUrl,
} from '../ticketingApi';
import { normalizeFileName } from '../../../components/ui/utils/fileNameEncoding';
import { TicketingOriginalDescriptionFooter } from './TicketingOriginalDescriptionFooter';

function authorLabel(msg, requesterUserId) {
  const name =
    msg.author?.userDisplayName || msg.author?.userEmail || `User #${msg.author?.id ?? '?'}`;
  const rid = requesterUserId != null ? Number(requesterUserId) : null;
  const aid = msg.author?.id != null ? Number(msg.author.id) : null;
  if (rid != null && aid === rid) return { name, role: 'requester' };
  return { name, role: 'it' };
}

/**
 * Chat-style comments for a ticket: requester ↔ IT (any authenticated staff).
 * @param {{ id: number, ticketRequestedFromUserId?: number|null }} ticket
 */
export function TicketingConversationModal({ isOpen, onClose, ticket }) {
  const { theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const listEndRef = useRef(null);

  const ticketId = ticket?.id;

  const scrollToBottom = useCallback(() => {
    listEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, []);

  const load = useCallback(async () => {
    if (ticketId == null) return;
    setLoading(true);
    setSendError(null);
    try {
      const rows = await fetchTicketingRequestMessages(ticketId);
      setMessages(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setSendError(e?.message || 'Could not load messages. Sign in and try again.');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (isOpen && ticketId != null) {
      load();
      setDraft('');
    }
  }, [isOpen, ticketId, load]);

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [isOpen, messages.length, scrollToBottom]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || ticketId == null) return;
    setSending(true);
    setSendError(null);
    try {
      const created = await postTicketingRequestMessage(ticketId, text);
      setDraft('');
      setMessages((prev) => [...prev, created]);
    } catch (e) {
      setSendError(e?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (!ticket) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>
        <ModalTitle>Conversation · Ticket #{ticket.id}</ModalTitle>
        <ModalDescription>
          Full message history for this ticket.
        </ModalDescription>
      </ModalHeader>
      <ModalContent className="flex flex-col gap-4 max-h-[65vh]">
        <div
          className={cn(
            'flex-1 min-h-[200px] max-h-[38vh] overflow-y-auto rounded-xl border p-3 space-y-3',
            theme.border.light,
            theme.colors.background
          )}
        >
          {loading ? (
            <p className={cn('text-sm text-center py-8', theme.text.muted)}>Loading messages…</p>
          ) : messages.length === 0 ? (
            <p className={cn('text-sm text-center py-8', theme.text.muted)}>
              No messages yet. Start the conversation below.
            </p>
          ) : (
            messages.map((msg) => {
              const { name, role } = authorLabel(msg, ticket.ticketRequestedFromUserId);
              const isStaffMessage = role === 'it';
              return (
                <div
                  key={msg.id}
                  className={cn('flex w-full', isStaffMessage ? 'justify-end' : 'justify-start')}
                >
                  <Card
                    className={cn(
                      'max-w-[92%] sm:max-w-[85%] border shadow-sm',
                      theme.border.light,
                      isStaffMessage
                        ? 'bg-blue-50/90 dark:bg-blue-950/35'
                        : theme.colors.surface
                    )}
                  >
                    <CardContent className="px-3 py-2.5 pt-3 pb-3">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={cn('text-xs font-semibold', theme.text.primary)}>{name}</span>
                        <Badge variant={role === 'requester' ? 'secondary' : 'default'} size="sm">
                          {role === 'requester' ? 'Requester' : 'IT / Staff'}
                        </Badge>
                        <span className={cn('text-[10px] ml-auto', theme.text.muted)}>
                          {msg.createdAt
                            ? new Date(msg.createdAt).toLocaleString(undefined, {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })
                            : ''}
                        </span>
                      </div>
                      <p className={cn('text-sm whitespace-pre-wrap break-words m-0', theme.text.primary)}>
                        {msg.body}
                      </p>
                      {Array.isArray(msg.attachments) && msg.attachments.length > 0 ? (
                        <ul className="mt-2 space-y-1 list-none p-0 m-0">
                          {msg.attachments.map((a) => (
                            <li key={a.id}>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto p-0 text-xs text-blue-600 dark:text-blue-400 hover:underline text-left"
                                onClick={async () => {
                                  let url;
                                  try {
                                    url = await downloadTicketingAttachmentObjectUrl(ticket.id, a.id);
                                    const dl = document.createElement('a');
                                    dl.href = url;
                                    dl.download = normalizeFileName(a.fileName || 'file');
                                    document.body.appendChild(dl);
                                    dl.click();
                                    dl.remove();
                                  } catch (e) {
                                    alert(e?.message || 'Download failed');
                                  } finally {
                                    if (url) URL.revokeObjectURL(url);
                                  }
                                }}
                              >
                                {normalizeFileName(a.fileName || '')}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </CardContent>
                  </Card>
                </div>
              );
            })
          )}
          <div ref={listEndRef} />
        </div>

        {sendError ? (
          <p className="text-sm text-red-600 dark:text-red-400 m-0">{sendError}</p>
        ) : null}

        <div>
          <Label htmlFor="ticketing-conv-draft" className="mb-1.5 block">
            Your message
          </Label>
          <Textarea
            id="ticketing-conv-draft"
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a reply…"
            disabled={sending}
            className="text-sm"
          />
        </div>

        <TicketingOriginalDescriptionFooter
          payload={ticket.payload}
          formKey={ticket.formKey}
        />
      </ModalContent>
      <ModalFooter className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={sending}>
          Close
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSend}
          disabled={sending || !draft.trim()}
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default TicketingConversationModal;
